import {
  User_Indicators,
  useSubscriptionUserIndicatorsSubscription,
} from '../graphql/generated/hasura';
import { useAuth } from '../auth/context/AuthContext';
import { createContext, useContext, useMemo } from 'react';

interface AuthProviderProps {
  children: React.ReactNode;
}

type UserIndicatorType = {
  inMail: boolean;
};

type IndicatorContextValue = {
  loggedUserIndicator: UserIndicatorType;
  accountsIndicatorById: Record<string, UserIndicatorType>;
};

const getIndicatorState = (
  indicators: User_Indicators[],
  type: string,
  userId?: string,
) =>
  indicators.find(
    (indicator) =>
      indicator.indicator_type === type &&
      (!userId || indicator.user_id === userId),
  )?.boolean_state || false;

export const UserIndicatorContext = createContext<IndicatorContextValue>(
  {} as IndicatorContextValue,
);

export const UserIndicatorProvider = ({ children }: AuthProviderProps) => {
  const { user, selectedAccount, accounts } = useAuth();

  const accountIds = useMemo(() => {
    return [
      user?.uuid,
      ...((accounts || [])?.map((account) => account.id).filter(Boolean) || []),
    ];
  }, [accounts, user?.uuid]);

  const { data } = useSubscriptionUserIndicatorsSubscription({
    fetchPolicy: 'network-only',
    skip: !(selectedAccount || user?.uuid),
    variables: {
      ids: accountIds,
    },
  });

  const indicatorsById = useMemo(() => {
    return accountIds.reduce((acc, id) => {
      if (!id) return acc;
      acc[id] = {
        inMail: getIndicatorState(
          (data?.user_indicators || []) as unknown as User_Indicators[],
          'IN_MAIL',
          id,
        ),
      };

      return acc;
    }, {} as Record<string, UserIndicatorType>);
  }, [accountIds, data?.user_indicators]);

  const contextValue = useMemo(() => {
    return {
      loggedUserIndicator:
        selectedAccount || user?.uuid
          ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            indicatorsById?.[(selectedAccount || user?.uuid) as string]
          : ({} as UserIndicatorType),
      accountsIndicatorById: indicatorsById,
    };
  }, [indicatorsById, selectedAccount, user?.uuid]);

  return (
    <UserIndicatorContext.Provider value={contextValue}>
      {children}
    </UserIndicatorContext.Provider>
  );
};

export const useIndicators = (): IndicatorContextValue => {
  return useContext(UserIndicatorContext);
};
